// === IMPORTS ===
import React, { useState, useEffect, useRef } from 'react';
import {
  OfficeLayout,
  OfficeRoom,
  RoomType,
  PlacedFurniture,
  AIAgent,
  FloorMaterialOption,
  MCPToolDefinition,
  FurnitureType,
} from '../types';
import { FURNITURE_CATALOG, AGENT_ROLES_CONFIG, DEFAULT_MCP_TOOLS } from '../data/officeCatalog';
import {
  X,
  Sliders,
  Trash2,
  Edit3,
  Check,
  RotateCw,
  Zap,
  Compass,
  Droplet,
  Users,
  Bot,
  Sparkles,
  Palette,
  Info,
  Maximize2,
  Box,
  ChevronLeft,
  ChevronRight,
  UserMinus,
  ShieldCheck,
  Tag,
  Eye,
} from 'lucide-react';
import { CustomAssetUploader } from './CustomAssetUploader';
import { FloorTexturePreviewBox } from './FloorTexturePreviewBox';
import AppIcon from './AppIcon';
import { findOverlappingRoom } from '../utils/isoMath';
import { resolveColorToHex } from '../utils/floorColorUtils';
import './OfficeInspectorDrawer.css';
import '../styles/twin-drawers-shared.css'; // ← agregar, aunque hoy "funcione" sin ella
import '../styles/twin-icons-shared.css';


export const ROOM_TYPE_OPTIONS: { id: RoomType; label: string; icon: string; defaultColor: string }[] = [
  { id: 'work', label: 'Development / Work', icon: 'laptop', defaultColor: '#3b82f6' },
  { id: 'meeting', label: 'Conference Room', icon: 'users', defaultColor: '#8b5cf6' },
  { id: 'human_interaction', label: 'Human Interaction', icon: 'user-check', defaultColor: '#f97316' },
  { id: 'breakroom', label: 'Cafeteria / Breakroom', icon: 'coffee', defaultColor: '#f59e0b' },
  { id: 'restroom', label: 'Restrooms', icon: 'bath', defaultColor: '#06b6d4' },
  { id: 'server_room', label: 'Servers & DevOps', icon: 'server', defaultColor: '#10b981' },
  { id: 'lounge', label: 'Quiet Lounge', icon: 'sofa', defaultColor: '#ec4899' },
  { id: 'reception', label: 'Reception / Lobby', icon: 'building', defaultColor: '#6366f1' },
  { id: 'custom', label: 'Custom', icon: 'sparkles', defaultColor: '#14b8a6' },
];

interface OfficeInspectorDrawerProps {
  isOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  onToggle?: () => void;
  activeStep: number;
  layout: OfficeLayout;
  onLayoutChange: (layout: OfficeLayout) => void;
  selectedRoomId: string | null;
  onSelectRoom: (roomId: string | null) => void;
  onOpenDeleteRoomModal: (room: OfficeRoom) => void;
  onOverlapPrevented?: (targetRoomName: string, conflictingRoomName: string) => void;
  selectedFloorType: string | null;
  setSelectedFloorType: (type: string | null) => void;
  floorMaterials: FloorMaterialOption[];
  onFloorMaterialsChange?: React.Dispatch<React.SetStateAction<FloorMaterialOption[]>>;
  onOpenEditFloorModal: (mat: FloorMaterialOption) => void;
  onOpenDeleteFloorModal?: (mat: FloorMaterialOption) => void;
  selectedItem: PlacedFurniture | null;
  onSelectItem: (item: PlacedFurniture | null) => void;
  selectedFurnitureType?: FurnitureType | null;
  setSelectedFurnitureType?: (type: FurnitureType | null) => void;
  mcpTools?: MCPToolDefinition[];
  onOpenDeleteFurnitureModal: (target: { type: FurnitureType; label?: string; icon?: string; id?: string }) => void;
  onRenameFurnitureType?: (type: FurnitureType, newName: string) => void;
  selectedAgent: AIAgent | null;
  onAgentSelect: (agent: AIAgent | null) => void;
  onOpenAgentSpriteModal: (agent: AIAgent) => void;
  onOpenDeleteAgentModal: (agent: AIAgent) => void;
}

// === COMPONENTE: DRAWER INSPECTOR (ZONA / PISO / MUEBLE / AGENTE) ===
export const OfficeInspectorDrawer: React.FC<OfficeInspectorDrawerProps> = ({
  isOpen,
  onClose,
  onOpen,
  onToggle,
  activeStep,
  layout,
  onLayoutChange,
  selectedRoomId,
  onSelectRoom,
  onOpenDeleteRoomModal,
  onOverlapPrevented,
  selectedFloorType,
  setSelectedFloorType,
  floorMaterials,
  onFloorMaterialsChange,
  onOpenEditFloorModal,
  onOpenDeleteFloorModal,
  selectedItem,
  onSelectItem,
  selectedFurnitureType,
  setSelectedFurnitureType,
  mcpTools = DEFAULT_MCP_TOOLS,
  onOpenDeleteFurnitureModal,
  onRenameFurnitureType,
  selectedAgent,
  onAgentSelect,
  onOpenAgentSpriteModal,
  onOpenDeleteAgentModal,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const tileSize = layout.tileSizeMeters ?? 1.0;

  const activeRoom = selectedRoomId ? layout.rooms?.find((r) => r.id === selectedRoomId) || null : null;
  const activeFloorMat = selectedFloorType ? floorMaterials.find((f) => f.id === selectedFloorType) || null : null;
  const activeCatalogFurniture = selectedFurnitureType
    ? FURNITURE_CATALOG.find((f) => f.type === selectedFurnitureType) || null
    : null;

  const inspectedType: 'room' | 'floor' | 'furniture' | 'agent' | null = (() => {
    if (activeStep === 1 && activeRoom) return 'room';
    if (activeStep === 2 && activeFloorMat) return 'floor';
    if (activeStep === 3 && (selectedItem || activeCatalogFurniture)) return 'furniture';
    if (activeStep === 4 && selectedAgent) return 'agent';

    if (selectedItem || activeCatalogFurniture) return 'furniture';
    if (selectedAgent) return 'agent';
    if (activeRoom) return 'room';
    if (activeFloorMat) return 'floor';

    return null;
  })();

  const isDrawerOpen = isOpen !== undefined ? Boolean(isOpen && inspectedType) : Boolean(inspectedType);

  // === CERRAR SIN LIMPIAR SELECCIÓN (clic afuera o colapsar con chevron) ===
  const handleCloseDrawer = () => {
    if (onClose) {
      onClose();
    } else {
      if (inspectedType === 'room') onSelectRoom(null);
      else if (inspectedType === 'floor') setSelectedFloorType(null);
      else if (inspectedType === 'furniture') {
        onSelectItem(null);
        if (setSelectedFurnitureType) setSelectedFurnitureType(null);
      } else if (inspectedType === 'agent') onAgentSelect(null);
    }
  };

  // === CERRAR Y DESELECCIONAR (botón X del header) ===
  const handleDeselectAndClose = () => {
    if (onClose) onClose();
    if (inspectedType === 'room' || selectedRoomId) onSelectRoom(null);
    if (inspectedType === 'floor' || selectedFloorType) setSelectedFloorType(null);
    if (inspectedType === 'furniture' || selectedItem || selectedFurnitureType) {
      onSelectItem(null);
      if (setSelectedFurnitureType) setSelectedFurnitureType(null);
    }
    if (inspectedType === 'agent' || selectedAgent) onAgentSelect(null);
  };

  // === CERRAR AL INTERACTUAR AFUERA DEL DRAWER ===
  useEffect(() => {
    if (!isDrawerOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (drawerRef.current && drawerRef.current.contains(target)) {
        return;
      }

      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) {
        return;
      }

      if (onClose) {
        onClose();
      } else {
        handleCloseDrawer();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDeselectAndClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawerOpen, onClose, inspectedType]);

  // --- Handlers de Zona ---
  const handleUpdateRoom = (updated: OfficeRoom) => {
    const rooms = layout.rooms || [];
    const previous = rooms.find((r) => r.id === updated.id);
    if (
      previous &&
      (previous.col !== updated.col ||
        previous.row !== updated.row ||
        previous.width !== updated.width ||
        previous.height !== updated.height)
    ) {
      const conflict = findOverlappingRoom(
        updated,
        rooms.filter((r) => r.id !== updated.id)
      );

      if (conflict) {
        if (onOverlapPrevented) {
          onOverlapPrevented(updated.name, conflict.name);
        }
        return;
      }
    }

    const updatedRooms = rooms.map((r) => (r.id === updated.id ? updated : r));
    onLayoutChange({
      ...layout,
      rooms: updatedRooms,
    });
  };

  // --- Handler de Toggle de Checkerboard de Piso ---
  const handleToggleFloorChecker = () => {
    if (!activeFloorMat || !onFloorMaterialsChange) return;
    const isCurrentlyChecker = activeFloorMat.hasCheckerboard !== false;
    onFloorMaterialsChange((prev) =>
      prev.map((f) => (f.id === activeFloorMat.id ? { ...f, hasCheckerboard: !isCurrentlyChecker } : f))
    );
  };

  // --- Handlers de Mobiliario ---
  const [editingFurnitureName, setEditingFurnitureName] = useState<string>('');
  const [isEditingFurnitureName, setIsEditingFurnitureName] = useState<boolean>(false);

  useEffect(() => {
    if (selectedItem) {
      const itemDef = FURNITURE_CATALOG.find((f) => f.type === selectedItem.type);
      setEditingFurnitureName(itemDef?.name || '');
      setIsEditingFurnitureName(false);
    } else if (activeCatalogFurniture) {
      setEditingFurnitureName(activeCatalogFurniture.name || '');
      setIsEditingFurnitureName(false);
    }
  }, [selectedItem?.type, selectedItem?.id, activeCatalogFurniture?.type]);

  const handleSaveRenameFurniture = (typeToRename?: FurnitureType) => {
    const targetType = typeToRename || selectedItem?.type || activeCatalogFurniture?.type;
    if (!targetType) return;
    const trimmed = editingFurnitureName.trim();
    if (trimmed) {
      const def = FURNITURE_CATALOG.find((f) => f.type === targetType);
      if (def) def.name = trimmed;
      if (onRenameFurnitureType) {
        onRenameFurnitureType(targetType, trimmed);
      }
    }
    setIsEditingFurnitureName(false);
  };

  const handleUpdateCatalogDimensions = (newW: number, newH: number) => {
    if (!activeCatalogFurniture) return;
    const cleanW = Math.max(1, Math.min(10, newW));
    const cleanH = Math.max(1, Math.min(10, newH));
    activeCatalogFurniture.width = cleanW;
    activeCatalogFurniture.height = cleanH;
    onLayoutChange({ ...layout });
  };

  const handleUpdateCatalogMcpTool = (toolId: string) => {
    if (!activeCatalogFurniture) return;
    activeCatalogFurniture.mcpToolId = toolId === 'none' ? undefined : toolId;
    onLayoutChange({ ...layout });
  };

  const handleUpdateCatalogCustomImage = (imageUri?: string) => {
    if (!activeCatalogFurniture) return;
    activeCatalogFurniture.customImage = imageUri;
    const updatedItems = layout.items.map((it) =>
      it.type === activeCatalogFurniture.type && !it.customImage ? { ...it, customImage: imageUri } : it
    );
    onLayoutChange({ ...layout, items: updatedItems });
  };

  const handleRotateSelectedItem = () => {
    if (!selectedItem) return;
    const nextRotations: Record<number, 0 | 90 | 180 | 270> = {
      0: 90,
      90: 180,
      180: 270,
      270: 0,
    };
    const newRotation = (nextRotations[selectedItem.rotation] || 0) as 0 | 90 | 180 | 270;    const updatedItem = { ...selectedItem, rotation: newRotation };
    const updatedItems = layout.items.map((it) => (it.id === selectedItem.id ? updatedItem : it));
    onLayoutChange({ ...layout, items: updatedItems });
    onSelectItem(updatedItem);
  };

  const handleUpdateItemDimensions = (newW: number, newH: number) => {
    if (!selectedItem) return;
    const cleanW = Math.max(1, Math.min(10, newW));
    const cleanH = Math.max(1, Math.min(10, newH));
    const updatedItem = { ...selectedItem, width: cleanW, height: cleanH };
    const updatedItems = layout.items.map((it) => (it.id === selectedItem.id ? updatedItem : it));
    onLayoutChange({ ...layout, items: updatedItems });
    onSelectItem(updatedItem);
  };

  const handleUpdateItemMcpTool = (toolId: string) => {
    if (!selectedItem) return;
    const val = toolId === 'none' ? undefined : toolId;
    const updatedItem = { ...selectedItem, mcpToolId: val };
    const updatedItems = layout.items.map((it) => (it.id === selectedItem.id ? updatedItem : it));
    onLayoutChange({ ...layout, items: updatedItems });
    onSelectItem(updatedItem);
  };

  const handleUpdateItemCustomImage = (imageUri?: string) => {
    if (!selectedItem) return;
    const updatedItem = { ...selectedItem, customImage: imageUri };
    const updatedItems = layout.items.map((it) => (it.id === selectedItem.id ? updatedItem : it));
    onLayoutChange({ ...layout, items: updatedItems });
    onSelectItem(updatedItem);
  };

  // --- Handlers de Agente ---
  const handleUpdateAgentNickname = (nickname: string) => {
    if (!selectedAgent) return;
    const updatedAgents = layout.agents.map((ag) =>
      ag.id === selectedAgent.id ? { ...ag, nickname } : ag
    );
    onLayoutChange({ ...layout, agents: updatedAgents });
    onAgentSelect({ ...selectedAgent, nickname });
  };

  const handleUpdateAgentCustomImage = (imageUri?: string) => {
    if (!selectedAgent) return;
    const updatedAgents = layout.agents.map((ag) =>
      ag.id === selectedAgent.id ? { ...ag, customImage: imageUri } : ag
    );
    onLayoutChange({ ...layout, agents: updatedAgents });
    onAgentSelect({ ...selectedAgent, customImage: imageUri });
  };

  if (!inspectedType) {
    return null;
  }

  const elementLabel = (() => {
    if (inspectedType === 'room') return activeRoom?.name || 'Zone / Room';
    if (inspectedType === 'floor') return activeFloorMat?.name || 'Floor';
    if (inspectedType === 'furniture') {
      if (selectedItem) {
      return FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.name || selectedItem.label || 'Resource';      }
      return activeCatalogFurniture?.name || 'Resource';
    }
    if (inspectedType === 'agent') return selectedAgent?.nickname || selectedAgent?.name || 'Agent';
    return 'Element';
  })();

  // === RENDER ===
  return (
    <aside
      ref={drawerRef}
      id="office-inspector-drawer"
      aria-label="Selected Element Configuration Panel"
      className={`twin-inspector ${isDrawerOpen ? 'twin-inspector--open' : 'twin-inspector--closed'}`}
    >
      {/* === BOTÓN DE COLAPSAR/EXPANDIR (borde izquierdo) === */}
      <button
        id="office-inspector-toggle-btn"
        type="button"
        onClick={() => {
          if (isDrawerOpen) {
            if (onClose) onClose();
            else if (onToggle) onToggle();
            else handleCloseDrawer();
          } else {
            if (onOpen) onOpen();
            else if (onToggle) onToggle();
          }
        }}
        className={`twin-inspector-toggle-btn ${isDrawerOpen ? 'twin-inspector-toggle-btn--open' : 'twin-inspector-toggle-btn--closed'}`}
        title={isDrawerOpen ? 'Collapse side configuration panel' : `Expand configuration panel: ${elementLabel}`}
        aria-label={isDrawerOpen ? 'Collapse side panel' : `Expand side panel: ${elementLabel}`}
      >
        {isDrawerOpen ? <ChevronRight className="twin-icon-sm" /> : <ChevronLeft className="twin-icon-sm twin-color-theme-accent" />}
      </button>

      {/* === HEADER === */}
      <div className="twin-drawer-header">
        <div className="twin-inspector-header-left">
          <div className="twin-drawer-icon-badge">

            {inspectedType === 'room' && (
              <AppIcon icon={activeRoom?.icon} name={activeRoom?.name} type={activeRoom?.type} className="twin-icon-sm twin-color-theme-accent" />
            )}
            {inspectedType === 'floor' && <Palette className="twin-icon-sm twin-color-amber" />}
            {inspectedType === 'furniture' && <Box className="twin-icon-sm twin-color-amber" />}
            {inspectedType === 'agent' && <Bot className="twin-icon-sm twin-color-emerald" />}
          </div>
          <div className="twin-drawer-header-text">
            <h2 className="twin-drawer-title ">
              {inspectedType === 'room' && 'Zone Configuration'}
              {inspectedType === 'floor' && 'Floor Configuration'}
              {inspectedType === 'furniture' && 'Resource Configuration'}
              {inspectedType === 'agent' && 'Agent Configuration'}
            </h2>
            <p className="twin-drawer-subtitle twin-drawer-subtitle--accent">
              {inspectedType === 'room' && activeRoom?.name}
              {inspectedType === 'floor' && activeFloorMat?.name}
              {inspectedType === 'furniture' && (FURNITURE_CATALOG.find((f) => f.type === selectedItem?.type)?.name || 'Resource')}
              {inspectedType === 'agent' && (selectedAgent?.nickname?.trim() || selectedAgent?.name)}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleDeselectAndClose}
          className="twin-inspector-close-btn"
          title="Deselect and close configuration panel (Esc)"
          aria-label="Deselect and close configuration panel"
        >
          <X className="twin-icon-sm" />
        </button>
      </div>

      {/* === CUERPO CON SCROLL === */}
      <div className="twin-drawer-body">
        {/* === CASO 1: INSPECTOR DE ZONA === */}
        {inspectedType === 'room' && activeRoom && (
          <div className="twin-inspector-section">
            <div className="twin-inspector-field-group">
            <span className="twin-field-label twin-inline-icon-label">
              <Tag className="twin-icon-xs twin-color-amber" />
              <h3 className="twin-section-heading">Zone Name</h3>
            </span>
            <div className="twin-inspector-field-row">
              <div className="twin-inspector-icon-box">
                <AppIcon icon={activeRoom.icon} name={activeRoom.name} type={activeRoom.type} className="twin-icon-sm twin-color-theme-accent" />
              </div>
              <input
                type="text"
                value={activeRoom.name}
                onChange={(e) => handleUpdateRoom({ ...activeRoom, name: e.target.value })}
                className="twin-input"
              />
            </div>
          </div>

            <div className="twin-inspector-grid-2">
              <div>
                <label className="twin-field-label twin-inspector-field-label--block">Type / Purpose</label>
                <select
                  value={activeRoom.type}
                  onChange={(e) => {
                    const t = e.target.value as RoomType;
                    const matched = ROOM_TYPE_OPTIONS.find((opt) => opt.id === t);
                    handleUpdateRoom({
                      ...activeRoom,
                      type: t,
                      icon: matched?.icon || activeRoom.icon,
                      color: matched?.defaultColor || activeRoom.color,
                    });
                  }}
                  className="twin-select"
                >
                  {ROOM_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="twin-field-label twin-inspector-field-label--block">Zone Color</label>
                <div className="twin-inspector-color-row">
                  <input
                    type="color"
                    value={activeRoom.color || '#38bdf8'}
                    onChange={(e) => handleUpdateRoom({ ...activeRoom, color: e.target.value })}
                    className="twin-inspector-color-input"
                  />
                  <span className="twin-inspector-color-hex">{activeRoom.color}</span>
                </div>
              </div>
            </div>

            <div className="twin-inspector-card">
              <div className="twin-inspector-metrics-header">
                <span className="twin-field-label twin-inline-icon-label ">
                  <Maximize2 className="twin-icon-xs twin-color-amber" />
                  <h3 className="twin-section-heading">Grid Space</h3>
                </span>
                <span className="twin-inspector-metrics-badge">
                  {((activeRoom.width * tileSize) * (activeRoom.height * tileSize)).toFixed(1)} m²
                </span>
              </div>

              <div className="twin-inspector-coord-grid">
                <div className="twin-inspector-coord-row">
                  <span className="twin-inspector-coord-label">Column (X):</span>
                  <input
                    type="number"
                    min={0}
                    max={Math.max(0, layout.gridWidth - activeRoom.width)}
                    value={activeRoom.col}
                    onChange={(e) => handleUpdateRoom({ ...activeRoom, col: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                    className="twin-inspector-coord-input"
                  />
                </div>

                <div className="twin-inspector-coord-row">
                  <span className="twin-inspector-coord-label">Row (Y):</span>
                  <input
                    type="number"
                    min={0}
                    max={Math.max(0, layout.gridHeight - activeRoom.height)}
                    value={activeRoom.row}
                    onChange={(e) => handleUpdateRoom({ ...activeRoom, row: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                    className="twin-inspector-coord-input"
                  />
                </div>

                <div className="twin-inspector-coord-row">
                  <span className="twin-inspector-coord-label">Width (W):</span>
                  <input
                    type="number"
                    min={1}
                    max={layout.gridWidth}
                    value={activeRoom.width}
                    onChange={(e) => handleUpdateRoom({ ...activeRoom, width: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    className="twin-inspector-coord-input "
                  />
                </div>

                <div className="twin-inspector-coord-row">
                  <span className="twin-inspector-coord-label">Length (H):</span>
                  <input
                    type="number"
                    min={1}
                    max={layout.gridHeight}
                    value={activeRoom.height}
                    onChange={(e) => handleUpdateRoom({ ...activeRoom, height: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    className="twin-inspector-coord-input "
                  />
                </div>
              </div>
            </div>

            <div className="twin-inspector-card">
            <span className="twin-field-label twin-inline-icon-label twin-inspector-field-label--amber">
              <Sliders className="twin-icon-xs twin-color-amber" />
              <span className="twin-section-heading">Agent Interaction Policies</span>
            </span>

              <div className="twin-inspector-policy-list">
                <div className="twin-inspector-policy-row">
                  <span className="twin-inspector-policy-label">
                    <Zap className="twin-icon-xs twin-color-amber" /> Energy Rate (+/- /s):
                  </span>
                  <input
                    type="number"
                    step="0.1"
                    value={activeRoom.policy.energyDelta}
                    onChange={(e) =>
                      handleUpdateRoom({ ...activeRoom, policy: { ...activeRoom.policy, energyDelta: parseFloat(e.target.value) || 0 } })
                    }
                    className="twin-inspector-policy-input"
                  />
                </div>

                <div className="twin-inspector-policy-row">
                  <span className="twin-inspector-policy-label">
                    <Compass className="twin-icon-xs twin-color-purple" /> Focus Bonus (%):
                  </span>
                  <input
                    type="number"
                    value={activeRoom.policy.focusBonus}
                    onChange={(e) =>
                      handleUpdateRoom({ ...activeRoom, policy: { ...activeRoom.policy, focusBonus: parseInt(e.target.value, 10) || 0 } })
                    }
                    className="twin-inspector-policy-input"
                  />
                </div>

                <div className="twin-inspector-policy-row">
                  <span className="twin-inspector-policy-label">
                    <Droplet className="twin-icon-xs twin-color-theme-accent" /> Restroom Need Rate (+/- /s):
                  </span>
                  <input
                    type="number"
                    step="0.1"
                    value={activeRoom.policy.bladderDelta}
                    onChange={(e) =>
                      handleUpdateRoom({ ...activeRoom, policy: { ...activeRoom.policy, bladderDelta: parseFloat(e.target.value) || 0 } })
                    }
                    className="twin-inspector-policy-input"
                  />
                </div>

                <div className="twin-inspector-policy-row">
                  <span className="twin-inspector-policy-label">
                    <Users className="twin-icon-xs twin-color-emerald" /> Max Allowed Capacity:
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={activeRoom.policy.maxCapacity || 6}
                    onChange={(e) =>
                      handleUpdateRoom({ ...activeRoom, policy: { ...activeRoom.policy, maxCapacity: parseInt(e.target.value, 10) || 6 } })
                    }
                    className="twin-inspector-policy-input"
                  />
                </div>

                <div className="twin-inspector-policy-text-field">
                  <label className="twin-inspector-policy-text-label">Ambient Status for Agent:</label>
                  <input
                    type="text"
                    value={activeRoom.policy.ambientStatusText || ''}
                    placeholder="e.g., In strategic meeting, Developing..."
                    onChange={(e) =>
                      handleUpdateRoom({ ...activeRoom, policy: { ...activeRoom.policy, ambientStatusText: e.target.value } })
                    }
                    className="twin-inspector-policy-text-input"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* === CASO 2: INSPECTOR DE PISO === */}
        {inspectedType === 'floor' && activeFloorMat && (
          <div className="twin-inspector-section">
            <div className="twin-inspector-floor-preview-block">
              <div className="twin-inspector-floor-preview-header">
                <span className="twin-field-label twin-inline-icon-label twin-no-margin">
                  <Eye className="twin-icon-xs" />
                  <span>Tile Visual Sample (1:1)</span>
                </span>
                <span className="twin-inspector-floor-preview-tag">Individual Tile</span>
              </div>
              <div className="twin-inspector-floor-preview-box">
                <FloorTexturePreviewBox material={activeFloorMat} isSelected={true} square={true} showBadge={true} />
              </div>
            </div>

            <div className="twin-inspector-card twin-inspector-card--sm">
              <span className="twin-field-label ">Floor Name</span>
              <input
                type="text"
                value={activeFloorMat.name}
                onChange={(e) => {
                  const newName = e.target.value;
                  if (onFloorMaterialsChange) {
                    onFloorMaterialsChange((prev) => prev.map((f) => (f.id === activeFloorMat.id ? { ...f, name: newName } : f)));
                  }
                }}
                className="twin-input"
              />
            </div>

            <div className="twin-inspector-card">
              <div className="twin-inspector-material-header">
                <span className="twin-field-label twin-no-margin">{activeFloorMat.name}</span>
                <span className="twin-field-label twin-no-margin">
                  {activeFloorMat.hasCheckerboard !== false ? 'Checkerboard Pattern' : 'Uniform Flat'}
                </span>
              </div>


              <div className="twin-inspector-material-details">
                <div className="twin-inspector-material-detail-row">
                  <span className="twin-inspector-material-detail-label">Texture Type:</span>
                  <span className="twin-inspector-material-detail-value">{activeFloorMat.customImage ? '🖼️ Custom Graphic' : '🎨 Procedural Color'}</span>
                </div>

                <div className="twin-inspector-material-detail-row">
                  <span className="twin-inspector-material-detail-label">Primary Color:</span>
                  <div className="twin-inspector-material-swatch-group">
                    <span className="twin-inspector-material-swatch" style={{ backgroundColor: resolveColorToHex(activeFloorMat.previewColor) }} />
                    <span className="twin-inspector-material-hex">{resolveColorToHex(activeFloorMat.previewColor)}</span>
                  </div>
                </div>

                {activeFloorMat.secondaryColor && (
                  <div className="twin-inspector-material-detail-row">
                    <span className="twin-inspector-material-detail-label">Secondary Color:</span>
                    <div className="twin-inspector-material-swatch-group">
                      <span className="twin-inspector-material-swatch" style={{ backgroundColor: resolveColorToHex(activeFloorMat.secondaryColor) }} />
                      <span className="twin-inspector-material-hex">{resolveColorToHex(activeFloorMat.secondaryColor)}</span>
                    </div>
                  </div>
                )}

                {activeFloorMat.description && <p className="twin-inspector-material-desc">{activeFloorMat.description}</p>}
              </div>
            </div>

            <div className="twin-inspector-toggle-card">
              <div className="twin-inspector-toggle-info">
                <span className="twin-inspector-toggle-title">Checkerboard Pattern</span>
                <span className="twin-inspector-toggle-desc">Toggle brightness or secondary tone on alternating cells</span>
              </div>
              <button
                type="button"
                onClick={handleToggleFloorChecker}
                className={`twin-inspector-toggle-btn-el ${activeFloorMat.hasCheckerboard !== false ? 'twin-inspector-toggle-btn-el--enabled' : 'twin-inspector-toggle-btn-el--disabled'}`}
              >
                {activeFloorMat.hasCheckerboard !== false ? 'Enabled' : 'Disabled'}
              </button>
            </div>

            <CustomAssetUploader
              key={activeFloorMat.id}
              title={`Texture for ${activeFloorMat.name || 'Floor'}`}
              subtitle="Upload a custom image or texture graphic to apply to this material."
              currentImage={activeFloorMat.customImage}
              onImageChange={(imgUrl) => {
                if (onFloorMaterialsChange) {
                  onFloorMaterialsChange((prev) => prev.map((f) => (f.id === activeFloorMat.id ? { ...f, customImage: imgUrl } : f)));
                }
              }}
              defaultIcon={activeFloorMat.icon || '🎨'}
            />

            <button type="button" onClick={() => onOpenEditFloorModal(activeFloorMat)} className="twin-inspector-gradient-btn">
              <Sliders className="twin-icon-sm" />
              <span>Open Advanced Floor Editor</span>
            </button>

            {onOpenDeleteFloorModal && (
              <div className="twin-inspector-danger-card">
                <div className="twin-inspector-danger-text">
                  <span className="twin-inspector-danger-label">Danger Zone</span>
                  <span className="twin-inspector-danger-desc">Delete floor and remove its tiles from the layout</span>
                </div>
                <button type="button" onClick={() => onOpenDeleteFloorModal(activeFloorMat)} className="twin-inspector-danger-btn">
                  <Trash2 className="twin-icon-xs" />
                  <span>Delete</span>
                </button>
              </div>
            )}

            <div className="twin-inspector-tip">
              <Info className="twin-icon-sm twin-color-theme-accent" />
              <span>Click or drag across the isometric grid to paint tiles with this material.</span>
            </div>
          </div>
        )}

        {/* === CASO 3: INSPECTOR DE MOBILIARIO === */}
        {inspectedType === 'furniture' && (selectedItem || activeCatalogFurniture) && (
          <div className="twin-inspector-section">
            {(() => {
              const currentDef = selectedItem ? FURNITURE_CATALOG.find((f) => f.type === selectedItem.type) : activeCatalogFurniture;
              const displayName = currentDef?.name || (selectedItem ? selectedItem.type : 'Resource');
              const targetType = selectedItem ? selectedItem.type : activeCatalogFurniture?.type;

              return (
                <div className="twin-inspector-card twin-inspector-card--sm">
                  <div className="twin-inspector-rename-header">
                    <span className="twin-field-label twin-inline-icon-label twin-inspector-field-label--amber">
                      <Tag className="twin-icon-xs twin-color-theme-accent" />
                      <span>Resource Name {selectedItem ? '(Instance)' : '(Catalog)'}</span>
                    </span>
                    <span className="twin-inspector-rename-hint">{isEditingFurnitureName ? 'Press Enter to save' : 'Double-click to edit'}</span>
                  </div>

                  {isEditingFurnitureName ? (
                    <div className="twin-inspector-rename-edit-row">
                      <input
                        type="text"
                        autoFocus
                        value={editingFurnitureName}
                        onChange={(e) => setEditingFurnitureName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSaveRenameFurniture(targetType);
                          } else if (e.key === 'Escape') {
                            setIsEditingFurnitureName(false);
                          }
                        }}
                        onBlur={() => handleSaveRenameFurniture(targetType)}
                        className="twin-inspector-rename-input"
                      />
                      <button type="button" onClick={() => handleSaveRenameFurniture(targetType)} className="twin-inspector-rename-save-btn" title="Save Name">
                        <Check className="twin-icon-xs" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onDoubleClick={() => {
                        setEditingFurnitureName(displayName);
                        setIsEditingFurnitureName(true);
                      }}
                      className="twin-inspector-rename-display"
                      title="Double-click to rename"
                    >
                      <span className="twin-inspector-rename-display-name">{displayName}</span>
                      <span className="twin-inspector-rename-edit-icon">
                        <Edit3 className="twin-icon-xs" />
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            {selectedItem && (
              <div className="twin-inspector-position-card">
                <div>
                  <span className="twin-field-label">Location &amp; Orientation</span>
                  <span className="twin-inspector-position-value">
                    Pos: ({selectedItem.col}, {selectedItem.row}) • Rotation: {selectedItem.rotation}°
                  </span>
                </div>
                <button type="button" onClick={handleRotateSelectedItem} className="twin-inspector-rotate-btn" title="Rotate 90 degrees">
                  <RotateCw className="twin-icon-xs twin-color-amber" />
                  <span>Rotate 90°</span>
                </button>
              </div>
            )}


            <div className="twin-inspector-card twin-inspector-card--sm">
              <span className="twin-field-label twin-inline-icon-label twin-inspector-field-label--amber">
                <Maximize2 className="twin-icon-xs twin-color-theme-accent" />
                <span>Occupied Floor Blocks {selectedItem ? '(Instance)' : '(Default)'}</span>
              </span>
              <div className="twin-inspector-dims-row">
                <div className="twin-inspector-dims-group">
                  <span className="twin-inspector-dims-label">Width:</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={
                      selectedItem
                        ? selectedItem.width || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.width || 1
                        : activeCatalogFurniture?.width || 1
                    }
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10) || 1;
                      if (selectedItem) {
                        handleUpdateItemDimensions(val, selectedItem.height || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.height || 1);
                      } else if (activeCatalogFurniture) {
                        handleUpdateCatalogDimensions(val, activeCatalogFurniture.height);
                      }
                    }}
                    className="twin-inspector-dims-input"
                  />
                </div>
                <span className="twin-inspector-dims-x">x</span>
                <div className="twin-inspector-dims-group">
                  <span className="twin-inspector-dims-label">Length:</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={
                      selectedItem
                        ? selectedItem.height || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.height || 1
                        : activeCatalogFurniture?.height || 1
                    }
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10) || 1;
                      if (selectedItem) {
                        handleUpdateItemDimensions(selectedItem.width || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.width || 1, val);
                      } else if (activeCatalogFurniture) {
                        handleUpdateCatalogDimensions(activeCatalogFurniture.width, val);
                      }
                    }}
                    className="twin-inspector-dims-input"
                  />
                </div>
                <span className="twin-inspector-dims-total">
                  {selectedItem
                    ? (selectedItem.width || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.width || 1) *
                      (selectedItem.height || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.height || 1)
                    : (activeCatalogFurniture?.width || 1) * (activeCatalogFurniture?.height || 1)}{' '}
                  blocks
                </span>
              </div>
            </div>

            <div className="twin-inspector-card twin-inspector-card--sm">
              <span className="twin-field-label twin-inline-icon-label twin-inspector-field-label--amber">
                <Zap className="twin-icon-xs twin-color-theme-accent" />
                <span>Associated MCP Tool</span>
              </span>
              <select
                value={
                  selectedItem
                    ? selectedItem.mcpToolId || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.mcpToolId || 'none'
                    : activeCatalogFurniture?.mcpToolId || 'none'
                }
                onChange={(e) => {
                  if (selectedItem) {
                    handleUpdateItemMcpTool(e.target.value);
                  } else if (activeCatalogFurniture) {
                    handleUpdateCatalogMcpTool(e.target.value);
                  }
                }}
                className="twin-select"
              >
                <option value="none">None (No MCP tool)</option>
                <optgroup label="Available MCP Tools">
                  {mcpTools.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.category || 'General'})
                    </option>
                  ))}
                </optgroup>
              </select>
              {(() => {
                const curToolId = selectedItem
                  ? selectedItem.mcpToolId || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.mcpToolId
                  : activeCatalogFurniture?.mcpToolId;
                const tool = mcpTools.find((t) => t.id === curToolId);
                if (!tool) return null;
                return (
                  <div className="twin-inspector-mcp-detail-box">
                    <div className="twin-inspector-mcp-detail-header">
                      <span className="twin-inspector-mcp-detail-name">
                        <Zap className="twin-icon-xs twin-color-amber" />
                        <span>{tool.name}</span>
                      </span>
                      <span className="twin-inspector-mcp-detail-category">{tool.category}</span>
                    </div>
                    <div className="twin-inspector-mcp-detail-desc">{tool.description}</div>
                  </div>
                );
              })()}
            </div>

            <CustomAssetUploader
              title={selectedItem ? 'Graphic Asset for this Instance' : 'Default Graphic Asset'}
              subtitle={selectedItem ? 'Upload an image to replace the graphic representation of this placed element.' : 'Upload a custom default image for this resource type.'}
              currentImage={selectedItem ? selectedItem.customImage || FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.customImage : activeCatalogFurniture?.customImage}
              onImageChange={(imgUri) => {
                if (selectedItem) {
                  handleUpdateItemCustomImage(imgUri);
                } else if (activeCatalogFurniture) {
                  handleUpdateCatalogCustomImage(imgUri);
                }
              }}
              defaultIcon={selectedItem ? FURNITURE_CATALOG.find((f) => f.type === selectedItem.type)?.icon : activeCatalogFurniture?.icon}
            />

            <div className="twin-inspector-danger-card">
              <div className="twin-inspector-danger-text">
                <span className="twin-inspector-danger-label">Danger Zone</span>
                <span className="twin-inspector-danger-desc">Delete resource and all its instances from the layout</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const currentDef = selectedItem ? FURNITURE_CATALOG.find((f) => f.type === selectedItem.type) : activeCatalogFurniture;
                  const targetType = selectedItem ? selectedItem.type : activeCatalogFurniture?.type;
                  if (targetType) {
                    onOpenDeleteFurnitureModal({
                      id: selectedItem?.id || `catalog_${targetType}`,
                      type: targetType,
                      label: selectedItem?.label || currentDef?.name || targetType,
                      icon: currentDef?.icon || 'box',
                    });
                  }
                }}
                className="twin-inspector-danger-btn"
              >
                <Trash2 className="twin-icon-xs" />
                <span>Delete</span>
              </button>
            </div>

            {!selectedItem && activeCatalogFurniture && (
              <div className="twin-inspector-tip twin-inspector-tip--amber">
                <Info className="twin-icon-sm twin-color-amber" />
                <span>Click any grid tile or drag the resource from the sidebar to place it on the layout.</span>
              </div>
            )}
          </div>
        )}

        {/* === CASO 4: INSPECTOR DE AGENTE === */}
        {inspectedType === 'agent' && selectedAgent && (() => {
          const isAgentPlaced = selectedAgent.isPlaced !== false && selectedAgent.currentCol >= 0 && selectedAgent.currentRow >= 0;
          return (
            <div className="twin-inspector-section">
              <div className={`twin-inspector-agent-status-card ${isAgentPlaced ? 'twin-inspector-agent-status-card--placed' : 'twin-inspector-agent-status-card--standby'}`}>
                <div className="twin-inspector-agent-status-top">
                  <div className="twin-inspector-agent-status-left">
                    <span className="twin-inspector-agent-status-dot-wrap">
                      <span className={`twin-inspector-agent-status-ping ${isAgentPlaced ? 'twin-inspector-agent-status-ping--placed' : 'twin-inspector-agent-status-ping--standby'}`} />
                      <span className={`twin-inspector-agent-status-dot ${isAgentPlaced ? 'twin-inspector-agent-status-dot--placed' : 'twin-inspector-agent-status-dot--standby'}`} />
                    </span>
                    <span className="twin-inspector-agent-status-label">{isAgentPlaced ? 'Status: Active on Grid' : 'Status: Inactive (On Bench)'}</span>
                  </div>
                  <span className={`twin-inspector-agent-status-badge ${isAgentPlaced ? 'twin-inspector-agent-status-badge--placed' : 'twin-inspector-agent-status-badge--standby'}`}>
                    {isAgentPlaced ? `Grid (${Math.round(selectedAgent.currentCol)}, ${Math.round(selectedAgent.currentRow)})` : 'Waiting Bench'}
                  </span>
                </div>

                <p className="twin-inspector-agent-status-desc">
                  {isAgentPlaced
                    ? 'This agent is active and placed on the grid. Agents are permanent members of the system and cannot be deleted. You can deactivate it to return it to the waiting bench.'
                    : 'The agent is currently inactive on the waiting bench. To activate and place it on the layout, drag it from Step 4 to any grid tile.'}
                </p>

                {isAgentPlaced ? (
                  <button type="button" onClick={() => onOpenDeleteAgentModal(selectedAgent)} className="twin-inspector-agent-deactivate-btn">
                    <UserMinus className="twin-icon-xs" />
                    <span>Deactivate and Remove from Grid</span>
                  </button>
                ) : (
                  <div className="twin-inspector-agent-standby-note">
                    <ShieldCheck className="twin-icon-xs" />
                    <span>Agents cannot be deleted; activate them by placing them on the grid.</span>
                  </div>
                )}
              </div>

              <div className="twin-inspector-card twin-inspector-card--sm">
                <div className="twin-inspector-nickname-header">
                  <label className="twin-field-label twin-inspector-field-label--emerald">Nickname / Name on Grid</label>
                  <span className="twin-inspector-nickname-hint">Text on layout</span>
                </div>
                <input
                  type="text"
                  value={selectedAgent.nickname ?? selectedAgent.name}
                  onChange={(e) => handleUpdateAgentNickname(e.target.value)}
                  placeholder="e.g., Leo DevOps"
                  className="twin-inspector-nickname-input"
                />
                <p className="twin-inspector-nickname-desc">This is the identification label rendered in real time above the avatar on the isometric canvas.</p>
              </div>

              <div className="twin-inspector-card twin-inspector-card--sm">
                <div className="twin-inspector-role-header">
                  <span className="twin-inspector-role-title">{AGENT_ROLES_CONFIG[selectedAgent.role]?.title || 'Agent'}</span>
                  <span className="twin-inspector-role-tag">Role: {selectedAgent.role}</span>
                </div>
<p className="twin-inspector-role-desc">{AGENT_ROLES_CONFIG[selectedAgent.role]?.defaultPrompt}</p>              </div>

              <div className="twin-inspector-card">
                <div className="twin-inspector-sprite-header">
                  <label className="twin-field-label twin-inline-icon-label twin-inspector-field-label--indigo">
  <Sparkles className="twin-icon-xs twin-color-indigo" />
  <span>16-Sprite Movement Asset</span>
</label>
                  <span className="twin-inspector-sprite-count">
                    {selectedAgent.spriteSet && Object.keys(selectedAgent.spriteSet).length > 0
                      ? `${Object.keys(selectedAgent.spriteSet).length}/16 sprites configured`
                      : '16 automatic generated sprites'}
                  </span>
                </div>
                <p className="twin-inspector-sprite-desc">Configure the 16 sprite frames (4 directions x 4 walk/idle steps) for smooth avatar movement on the isometric canvas.</p>
                <button type="button" onClick={() => onOpenAgentSpriteModal(selectedAgent)} className="twin-inspector-sprite-btn">
                  <Sparkles className="twin-icon-xs" />
                  <span>Configure Agent 16 Sprites</span>
                </button>
              </div>

              <CustomAssetUploader
                title="Alternative Static Image (Fallback)"
                subtitle="Single static image if the 16 sprites are not configured."
                currentImage={selectedAgent.customImage}
                onImageChange={handleUpdateAgentCustomImage}
                defaultIcon={AGENT_ROLES_CONFIG[selectedAgent.role]?.icon || 'bot'}
              />
            </div>
          );
        })()}
      </div>

      {/* === FOOTER === */}
      <div className="twin-drawer-footer">
        <button type="button" onClick={handleCloseDrawer} className="twin-btn-cancel">
          Deselect
        </button>

        {inspectedType === 'room' && activeRoom && (
          <button type="button" onClick={() => onOpenDeleteRoomModal(activeRoom)} className="twin-btn-save twin-btn-save--danger">
            <Trash2 className="twin-icon-xs" />
            <span>Delete Zone</span>
          </button>
        )}

        {inspectedType === 'furniture' && (selectedItem || activeCatalogFurniture) && (
          <button
            type="button"
            onClick={() => {
              const currentDef = selectedItem ? FURNITURE_CATALOG.find((f) => f.type === selectedItem.type) : activeCatalogFurniture;
              const targetType = selectedItem ? selectedItem.type : activeCatalogFurniture?.type;
              if (targetType) {
                onOpenDeleteFurnitureModal({
                  id: selectedItem?.id || `catalog_${targetType}`,
                  type: targetType,
                  label: selectedItem?.label || currentDef?.name || targetType,
                  icon: currentDef?.icon || 'box',
                });
              }
            }}
            className="twin-btn-save twin-btn-save--danger"
          >
            <Trash2 className="twin-icon-xs" />
            <span>Delete Resource</span>
          </button>
        )}

        {inspectedType === 'agent' && selectedAgent && (() => {
          const isAgentPlaced = selectedAgent.isPlaced !== false && selectedAgent.currentCol >= 0 && selectedAgent.currentRow >= 0;
          return isAgentPlaced ? (
            <button type="button" onClick={() => onOpenDeleteAgentModal(selectedAgent)} className="twin-btn-save twin-btn-save--danger">
              <UserMinus className="twin-icon-xs" />
              <span>Deactivate from Grid</span>
            </button>
          ) : (
            <span className="twin-inspector-standby-footer-badge">
              <span className="twin-inspector-standby-dot" />
              Inactive on Bench
            </span>
          );
        })()}

        {inspectedType === 'floor' && activeFloorMat && (
          <div className="twin-inspector-floor-footer-stack">
              <div className="twin-inspector-floor-footer-row">
                {onOpenDeleteFloorModal && (
                  <button type="button" onClick={() => onOpenDeleteFloorModal(activeFloorMat)} className="twin-btn-save twin-btn-save--danger">
                    <Trash2 className="twin-icon-xs" />
                    <span>Delete Floor</span>
                  </button>
                )}
                <button type="button" onClick={() => onOpenEditFloorModal(activeFloorMat)} className="twin-btn-save">
                  <Sliders className="twin-icon-xs" />
                  <span>Advanced Editor</span>
                </button>
             </div>
           </div>
          )}
      </div>
    </aside>
  );
};